import { useCallback, useEffect, useRef, useState } from 'react';
import { createRepeatedTaxRecalculator, useRepeatRowsHandler } from '@/lib/table-handlers';
import { useHoaDonBanHangSearchContext } from '../../../context';
import { usePriceLookup } from '../../../hooks/usePriceLookup';
import type { DiscountAllocationMode } from '../types';
import { discountAllocationRows } from '../utils';
import { calculateTotals } from '../../../utils';
import { useDetailRows } from './useDetailRows';
import type { VatTu } from '@/types/schemas';
import { QUERY_KEYS } from '@/constants';
import { useCRUD } from '@/hooks';

const repeatedTaxRecalculator = createRepeatedTaxRecalculator<BanHangRecord>({
  getTaxBase: row => Number(row?.tien_tck_nt ?? row?.tien_nt2 ?? 0) - Number(row?.ck_nt || 0),
  roundValue: value => Math.round(value * 10) / 10
});

const hasFieldChanges = (row: BanHangSearchParams, fields: BanHangSearchParams) =>
  Object.entries(fields).some(([field, value]) => !Object.is(row[field], value));

export const useDetailSection = ({
  initialRows,
  discountType,
  discountPool
}: {
  initialRows: BanHangRecord[];
  discountType: string;
  discountPool: number;
}) => {
  const [lastAllocationMode, setLastAllocationMode] = useState<DiscountAllocationMode | null>(null);
  const lastEditedFieldRef = useRef<string | null>(null);
  const { fetchDetail } = useCRUD({ endpoint: QUERY_KEYS.VAT_TU, initialFetch: false });
  const { customerGroupUuid } = useHoaDonBanHangSearchContext();
  const previousCustomerGroupUuidRef = useRef<string | undefined>(undefined);
  const detail = useDetailRows(initialRows, discountType, discountPool);
  const priceLookup = usePriceLookup();

  useEffect(() => {
    detail.setPriceLookupHandler(priceLookup);
  }, [detail.setPriceLookupHandler, priceLookup]);

  useEffect(() => {
    if (!detail.rows.length) return;

    const previousCustomerGroupUuid = previousCustomerGroupUuidRef.current;
    if (previousCustomerGroupUuid === customerGroupUuid) return;
    previousCustomerGroupUuidRef.current = customerGroupUuid;

    const updateRow = (uuid: string, fields: BanHangSearchParams) => {
      detail.setRows(previousRows =>
        previousRows.map(row => {
          if (row.uuid !== uuid) return row;
          if (!hasFieldChanges(row, fields)) return row;
          return { ...row, ...fields };
        })
      );
    };

    detail.rows.forEach(row => {
      if (!row.uuid) return;
      priceLookup(row, 'ma_vt_data', row.ma_vt_data, updateRow, { isInitializing: false });
    });
  }, [customerGroupUuid, detail.rows, detail.setRows, priceLookup]);

  const materialSelection = useCallback(
    async (anchorRowUuid: string, selectedRows: VatTu[]) => {
      if (!selectedRows.length) return;

      if (selectedRows.length > 1) {
        detail.replaceRowWithFieldValues(anchorRowUuid, 'ma_vt_data', selectedRows);
        return;
      }

      const [selectedRow] = selectedRows;
      if (!selectedRow?.uuid) return;

      const response = await fetchDetail(selectedRow.uuid);
      const material = Array.isArray(response) ? response[0] : response;
      detail.replaceRowWithFieldValues(anchorRowUuid, 'ma_vt_data', [material || selectedRow]);
    },
    [detail.replaceRowWithFieldValues, fetchDetail]
  );

  const repeatedRows = useRepeatRowsHandler({
    setRows: detail.setRows,
    onAfterRepeat: repeatedTaxRecalculator
  });

  const discountAllocation = useCallback(
    (mode: DiscountAllocationMode) => {
      const rows = discountAllocationRows(detail.rows, discountPool, mode);
      if (rows === detail.rows) return;

      setLastAllocationMode(mode);
      detail.setRows(rows);
    },
    [detail.rows, detail.setRows, discountPool]
  );

  const cellValueChange = useCallback(
    (rowUuid: string, field: string, value: BanHangValue) => {
      lastEditedFieldRef.current = field === 'gia_nt2' ? field : null;
      detail.handleCellValueChange(rowUuid, field, value);
    },
    [detail.handleCellValueChange]
  );

  useEffect(() => {
    if (discountType !== '3' || lastEditedFieldRef.current !== 'gia_nt2') return;

    discountAllocation(lastAllocationMode ?? 'value');
    lastEditedFieldRef.current = null;
  }, [detail.rows, discountAllocation, discountPool, discountType, lastAllocationMode]);

  return {
    rows: detail.rows,
    setRows: detail.setRows,
    totals: calculateTotals(detail.rows),
    tableProps: {
      rows: detail.rows,
      selectedRowUuid: detail.selectedRowUuid,
      selectedCell: detail.selectedCell,
      onRowClick: detail.handleRowClick as BanHangRecord,
      onCellClick: detail.handleCellClick as BanHangRecord,
      onAddRow: detail.handleAddRow,
      onDeleteRow: detail.handleDeleteRow,
      onCopyRow: detail.handleCopyRow,
      onPasteRow: detail.handlePasteRow,
      onMoveRow: detail.handleMoveRow,
      onAllocateDiscount: discountAllocation,
      onRepeatForRows: repeatedRows,
      onCellValueChange: cellValueChange,
      onVatTuSelection: materialSelection,
      handleUpdateNonWritableFields: detail.setNonWritableFields,
      handleUpdatePasteConfigs: detail.setPasteConfigs,
      isPasting: detail.isPasting
    }
  };
};
